"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateOnCallTimeBreakdown = calculateOnCallTimeBreakdown;
const luxon_1 = require("luxon");
/**
 * Calculates the breakdown of ON_CALL vs CALLED_IN time periods for an on-call shift
 * with multiple clock entries.
 *
 * @param shiftStart - The start time of the shift
 * @param shiftEnd - The end time of the shift
 * @param clockEntries - Array of clock in/out entries with meal break information
 * @param mealBreakMinutes - Duration of meal break in minutes (default: 30)
 * @returns Breakdown of time events and aggregated totals
 */
function calculateOnCallTimeBreakdown(shiftStart, shiftEnd, clockEntries, mealBreakMinutes = 30) {
    const events = [];
    if (!clockEntries || clockEntries.length === 0) {
        return {
            events: [],
            totalOnCallMinutes: 0,
            totalCalledInMinutes: 0,
            totalMealBreakMinutes: 0,
        };
    }
    const sortedEntries = [...clockEntries].sort((a, b) => a.clockInTime.getTime() - b.clockInTime.getTime());
    const shiftStartDt = luxon_1.DateTime.fromJSDate(shiftStart);
    const shiftEndDt = luxon_1.DateTime.fromJSDate(shiftEnd);
    // Add initial ON_CALL period: shift start → first clock in
    const firstClockInDt = luxon_1.DateTime.fromJSDate(sortedEntries[0].clockInTime);
    const onCallMinutesBefore = Math.round(firstClockInDt.diff(shiftStartDt, 'minutes').minutes);
    if (onCallMinutesBefore > 0) {
        events.push({
            type: 'ON_CALL',
            startTime: shiftStart,
            endTime: sortedEntries[0].clockInTime,
            minutesWorked: onCallMinutesBefore,
            mealBreakMinutes: 0,
        });
    }
    // Process each clock entry and gaps between them
    for (let i = 0; i < sortedEntries.length; i++) {
        const entry = sortedEntries[i];
        const clockInDt = luxon_1.DateTime.fromJSDate(entry.clockInTime);
        const clockOutDt = luxon_1.DateTime.fromJSDate(entry.clockOutTime);
        const calledInMinutes = Math.round(clockOutDt.diff(clockInDt, 'minutes').minutes);
        const entryMealBreakMinutes = entry.tookMealBreak
            ? mealBreakMinutes
            : 0;
        // Add CALLED_IN event
        events.push({
            type: 'CALLED_IN',
            startTime: entry.clockInTime,
            endTime: entry.clockOutTime,
            minutesWorked: calledInMinutes - entryMealBreakMinutes,
            mealBreakMinutes: entryMealBreakMinutes,
        });
        // Add ON_CALL period between this and next entry (if exists)
        if (i < sortedEntries.length - 1) {
            const nextEntry = sortedEntries[i + 1];
            const nextClockInDt = luxon_1.DateTime.fromJSDate(nextEntry.clockInTime);
            const onCallMinutesBetween = Math.round(nextClockInDt.diff(clockOutDt, 'minutes').minutes);
            if (onCallMinutesBetween > 0) {
                events.push({
                    type: 'ON_CALL',
                    startTime: entry.clockOutTime,
                    endTime: nextEntry.clockInTime,
                    minutesWorked: onCallMinutesBetween,
                    mealBreakMinutes: 0,
                });
            }
        }
    }
    // Add final ON_CALL period: last clock out → shift end
    const lastEntry = sortedEntries[sortedEntries.length - 1];
    const lastClockOutDt = luxon_1.DateTime.fromJSDate(lastEntry.clockOutTime);
    const onCallMinutesAfter = Math.round(shiftEndDt.diff(lastClockOutDt, 'minutes').minutes);
    if (onCallMinutesAfter > 0) {
        events.push({
            type: 'ON_CALL',
            startTime: lastEntry.clockOutTime,
            endTime: shiftEnd,
            minutesWorked: onCallMinutesAfter,
            mealBreakMinutes: 0,
        });
    }
    const totalOnCallMinutes = events
        .filter((e) => e.type === 'ON_CALL')
        .reduce((sum, e) => sum + e.minutesWorked, 0);
    const totalCalledInMinutes = events
        .filter((e) => e.type === 'CALLED_IN')
        .reduce((sum, e) => sum + e.minutesWorked, 0);
    const totalMealBreakMinutes = events.reduce((sum, e) => sum + e.mealBreakMinutes, 0);
    return {
        events,
        totalOnCallMinutes,
        totalCalledInMinutes,
        totalMealBreakMinutes,
    };
}
//# sourceMappingURL=on-call-time-calculation.js.map